import {
  Modal,
  Button,
  toast,
  BarsLoader,
  ModalProps,
} from '@insight-health-ai/roseate/components';
import { Microphone01, Star06 } from '@insight-health-ai/roseate/icons';
import { XFilled } from '@ant-design/icons';
import { useCallback, useEffect, useState, Fragment } from 'react';
import { analytics } from 'src/services';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';
import { Select } from 'antd';
import { doStream, endStream } from 'src/modules/ambient_solo/utils/listen';
import {
  useCreateEncounter,
  useStartEncounter,
  useUpdateEncounterDetails,
  useLegacyEncounterStore,
  useCompleteEncounter,
} from 'src/hooks/encounter';
import { useGetCurrentPractitioner } from 'src/hooks/settings';
import {
  useGetSpecialtyDemoConversation,
  FallbackDemoConversation,
  useCompleteOnboardingDemo,
  useLegacyPractitionerStore,
} from 'src/hooks/practitioner';
import { Encounter, TranscriptionMessage } from 'src/models';
import { combineTranscripts } from 'src/utils';
import moment from 'moment';
import AmbientAnimation from 'src/components/ambientAnimation';
import AmbientAnimationPaused from 'src/modules/ambient_solo/components/CurrentEncounter/AmbientAnimationPaused';
import { MicrophoneLevel } from 'src/modules/ambient_solo/components/baseComponents/MicrophoneLevel';
import { useMicrophoneBadge } from 'src/hooks/vim/hooks/useMicrophoneBadge';

const DEMO_ENCOUNTER_PHASE = 'in-visit';

type EncounterDemoProps = {
  isRetryAttempt?: boolean;
  open: boolean;
  onClose: () => void;
  /**
   * @todo: Harshit | remove this post migration
   */
  legacySearchFilters: unknown;
};

export const EncounterDemo = ({
  isRetryAttempt,
  open,
  onClose,
  legacySearchFilters,
}: EncounterDemoProps) => {
  const { user } = useAuth();
  const { data: currentPractitioner } = useGetCurrentPractitioner();
  const [encounter, setEncounter] = useState<Encounter | null>(null);

  const { isEntitled: isReferralsModuleEntitled } =
    useFeatureEntitlements('referral');
  const { isEntitled: isPhoneTriageModuleEntitled } =
    useFeatureEntitlements('phone-triage');

  const [stage, setStage] = useState<0 | 1 | 2>(0);
  const [attachMicOptionsListener, setAttachMicOptionsListener] =
    useState<boolean>(false);
  const [selectedMic, setSelectedMic] = useState<string | null>(null);
  const [micOptions, setMicOptions] = useState<MediaDeviceInfo[]>([]);
  const [recording, setRecording] = useState<{
    active: boolean;
    since: number | null;
  }>({
    active: false,
    since: null,
  });

  useMicrophoneBadge(recording.active);

  const [micLevel, setMicLevel] = useState(0);
  const [isTranscriptTimeout, setIsTranscriptTimeout] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState<TranscriptionMessage[]>(
    []
  );
  const [livePartialTranscript, setLivePartialTranscript] = useState<
    TranscriptionMessage[]
  >([]);
  const [transcript, setTranscript] = useState<TranscriptionMessage[]>([]);

  const { fetchCurrentPractitionerEntitlements, fetchSettingsOrgFlag } =
    useLegacyPractitionerStore();
  const { mutateAsync: createEncounter } = useCreateEncounter();
  const { mutateAsync: startEncounter } = useStartEncounter();
  const { mutateAsync: updateEncounterDetails } = useUpdateEncounterDetails();
  const { mutateAsync: completeEncounter } = useCompleteEncounter();

  const { updateEncounterList } = useLegacyEncounterStore();

  const {
    data: specialtyDemoConversation,
    isLoading: isLoadingSpecialtyDemoConversation,
  } = useGetSpecialtyDemoConversation(
    {},
    {
      refetchOnWindowFocus: false,
    }
  );
  const { mutateAsync: completeOnboardingDemo } = useCompleteOnboardingDemo();

  const updateMicOptions = useCallback(async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioDevices = (devices || []).filter(
      (device) => device.kind === 'audioinput'
    );
    setMicOptions(audioDevices);

    let selectedMicId = null;
    if (selectedMic) {
      selectedMicId = audioDevices.find(
        (mic) => mic.deviceId === selectedMic
      )?.deviceId;
    }
    selectedMicId = (selectedMicId || audioDevices[0]?.deviceId) ?? null;
    setSelectedMic(selectedMicId);
  }, [selectedMic]);

  const toggleRecording = (value: boolean) => {
    setRecording({
      active: value,
      since: value ? Date.now() : null,
    });
  };

  const handleAllowMicrophone = async () => {
    analytics.track(
      `Clicked Allow Microphone In Aura ${isRetryAttempt ? 'Retry' : 'Onboarding'} Demo`,
      {
        email: user?.email,
      }
    );
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await updateMicOptions();
      setAttachMicOptionsListener(true);
      setStage(1);
    } catch (error) {
      toast.error(
        'Failed to access microphone. Please allow microphone access in your browser settings and try again.'
      );
    }
  };

  const handleCreateEncounter = async () => {
    analytics.track(
      `Clicked Start Recording A Demo Session In Aura ${isRetryAttempt ? 'Retry' : 'Onboarding'} Demo`,
      {
        email: user?.email,
      }
    );
    try {
      const currentDate = new Date();
      const formattedDate = `${currentDate.getMonth() + 1}/${currentDate.getDate()}`;
      const description = `Demo Encounter - ${formattedDate}`;
      const encounter = await createEncounter({
        practitioner_id: currentPractitioner?.practitioner_id!,
        description: description,
        start_time: currentDate.toISOString(),
      });
      setEncounter(encounter);
      setStage(2);
    } catch (error) {
      toast.error('Failed to create encounter');
    }
  };

  const handleStartRecording = async () => {
    analytics.track(
      `Clicked Start Recording In Aura ${isRetryAttempt ? 'Retry' : 'Onboarding'} Demo`,
      {
        email: user?.email,
      }
    );

    if (!encounter) {
      toast.error('Something went wrong. Please try refreshing the page.');
      return;
    }

    try {
      if (encounter?.in_visit_status === 'new') {
        await startEncounter({
          encounterId: encounter.encounter_id,
          encounterPhase: DEMO_ENCOUNTER_PHASE,
          payload: {},
        });
      } else {
        await updateEncounterDetails({
          encounterId: encounter.encounter_id,
          encounterPhase: DEMO_ENCOUNTER_PHASE,
          payload: {},
        });
      }

      /**
       * @todo: Harshit | remove this post migration
       */
      try {
        updateEncounterList(legacySearchFilters);
      } catch (error) {
        // do nothing
      }

      await new Promise((resolve, reject) => {
        (doStream as any)({
          encounterId: encounter.encounter_id,
          encounterPhase: DEMO_ENCOUNTER_PHASE,
          setMicLevel,
          setLiveTranscript,
          setLivePartialTranscript,
          selectedMicrophoneId: selectedMic,
          successCallback: () => {
            resolve(true);
          },
          failureCallback: (error: any) => {
            reject(error);
          },
          setDictation: () => {},
          isDictate: false,
          transcriptTimeoutCallback: (error: string) => {
            if (error === 'NO_TRANSCRIPTION_RECEIVED') {
              setIsTranscriptTimeout(true);
            } else if (error !== 'NO_TRANSCRIPTION_RECEIVED') {
              setIsTranscriptTimeout(false);
            }
          },
        });
      });

      toggleRecording(true);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleStopRecording = async () => {
    try {
      await completeEncounter({
        encounterId: encounter?.encounter_id!,
        encounterPhase: DEMO_ENCOUNTER_PHASE,
      });
      await completeOnboardingDemo();
      endStream();
      toggleRecording(false);
      setLivePartialTranscript([]);
      setLiveTranscript([]);
      try {
        /**
         * @todo: Harshit | remove this post migration
         */
        await updateEncounterList(legacySearchFilters);
        await fetchCurrentPractitionerEntitlements();
        await fetchSettingsOrgFlag();
      } catch (error) {
        // do nothing
      }
      onClose();
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    }
  };

  useEffect(() => {
    setTranscript((transcript) => {
      return combineTranscripts({
        existingTranscripts: transcript,
        liveTranscript,
        sort: true,
      });
    });
  }, [liveTranscript, livePartialTranscript]);

  useEffect(() => {
    const listener = updateMicOptions;
    if (attachMicOptionsListener) {
      navigator.mediaDevices.addEventListener('devicechange', listener);
    }
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', listener);
    };
  }, [attachMicOptionsListener]);

  const stageJsx: Record<
    typeof stage,
    {
      content: React.ReactNode;
      title?: React.ReactNode;
      footer?: React.ReactNode;
      size?: ModalProps['size'];
    }
  > = {
    0: {
      content: (
        <div className="flex flex-col gap-4 p-4">
          <div className="text-lg font-semibold">
            Try a demo session Aura AI Scribe Yourself 🚀
          </div>
          <div>Welcome! 🌟</div>
          <div>
            Experience Aura AI Scribe hands-on and discover its powerful
            features.
          </div>
          <div>We can't wait to see what you'll create! 😊</div>
        </div>
      ),
      footer: (
        <div className="flex justify-center gap-4">
          <Button
            size="lg"
            icon={<Microphone01 />}
            onClick={handleAllowMicrophone}
          >
            Click here to allow microphone
          </Button>
        </div>
      ),
    },
    1: {
      content: (
        <div className="flex flex-col gap-4 p-4">
          <div className="text-lg font-semibold">
            Try a demo session Aura AI Scribe Yourself 🚀
          </div>
          <div>Welcome! 🌟</div>
          <div>
            Experience Aura AI Scribe hands-on and discover its powerful
            features.
          </div>
          <div>We can't wait to see what you'll create! 😊</div>
          <Select
            size="large"
            className="w-full"
            options={micOptions.map((mic) => ({
              label: mic.label,
              value: mic.deviceId,
            }))}
            value={selectedMic}
            onChange={(value) => setSelectedMic(value)}
          />
        </div>
      ),
      footer: (
        <div className="flex justify-center gap-4">
          <Button size="lg" icon={<Star06 />} onClick={handleCreateEncounter}>
            Start transcribing a demo session
          </Button>
        </div>
      ),
    },
    2: {
      size: 'lg',
      content: (() => {
        if (isLoadingSpecialtyDemoConversation) {
          return <BarsLoader />;
        }
        return (
          <div className="flex gap-6 flex-wrap lg:flex-nowrap justify-center items-center">
            <div className="flex flex-col gap-4 items-center justify-center min-w-[300px]">
              {recording.active ? (
                <>
                  <AmbientAnimation />
                  <Button
                    appearance="danger"
                    size="lg"
                    icon={<XFilled />}
                    onClick={handleStopRecording}
                  >
                    Stop transcribing
                  </Button>
                  <MicrophoneLevel
                    micLevel={micLevel}
                    isTranscriptTimeout={isTranscriptTimeout}
                  />
                </>
              ) : (
                <>
                  <AmbientAnimationPaused />
                  <Button
                    size="lg"
                    icon={<Microphone01 />}
                    onClick={handleStartRecording}
                  >
                    Start transcribing
                  </Button>
                </>
              )}
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2 text-center">
                <div className="text-lg font-semibold">
                  Read this entire script aloud now
                </div>
                <div className="text-sm text-gray-400">
                  Make sure you read both the clinician and patient parts!
                </div>
              </div>
              <div className="shadow-md rounded-md w-full h-56 bg-white overflow-y-auto">
                <div className="p-5">
                  {(specialtyDemoConversation || FallbackDemoConversation).map(
                    (line, index) => (
                      <div
                        id="specialty_conversation"
                        key={index}
                        className="mb-3 flex gap-2 text-justify"
                      >
                        {Object.keys(line).map((speaker) => (
                          <Fragment key={speaker}>
                            <div className="font-bold text-black">
                              {speaker}:
                            </div>
                            <div className="font-medium">{line[speaker]}</div>
                          </Fragment>
                        ))}
                      </div>
                    )
                  )}
                </div>
              </div>
              <div>
                {!recording.active ? (
                  <div className="text-sm text-gray-400 text-center">
                    Transcription will appear in real-time here
                  </div>
                ) : (
                  <div className="flex flex-col gap-3 max-h-56 pr-2 overflow-y-auto">
                    {transcript.map((item, index) => (
                      <div key={index} className="flex gap-1">
                        {item.timestamp && (
                          <div className="flex items-start font-medium flex-shrink-0">
                            {moment(item.timestamp).isSame(moment(), 'day')
                              ? moment(item.timestamp).format('h:mm a')
                              : moment(item.timestamp).format(
                                  'MMM DD, YYYY h:mm a'
                                )}
                            :
                          </div>
                        )}
                        <div className="text-sm text-gray-500">{item.text}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })(),
      footer: null,
    },
  };

  const { content, title, footer, size } = stageJsx[stage];
  return (
    <Modal
      closable={
        !encounter && (isReferralsModuleEntitled || isPhoneTriageModuleEntitled)
      }
      open={open}
      onClose={onClose}
      size={size}
      title={title}
      footer={footer}
    >
      {content}
    </Modal>
  );
};
